import { Divider, Stack, Typography } from '@mui/material'
import { CarrierVisitStatus } from '@operations/app/api'
import { WorkQueueStatusChip } from '@operations/components/chips/WorkQueueStatusChip'
import { useTranslate } from '@tolgee/react'
import { OptionsButton, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import {
  CarrierVisitCraneViewModel,
  CarrierVisitWorkQueueViewModel,
} from '../../models/work-instruction-view.model'
import { WorkQueueDetails } from '../atoms/WorkQueueDetails'
import { ControlRoomAccordion } from './ControlRoomAccordion'

interface Props {
  cranes: CarrierVisitCraneViewModel[]
  visitStatus: CarrierVisitStatus
  selectedCraneId?: number | null
  selectedWorkQueueId?: number | null
  setSelectedCraneId: (craneId: number) => void
  setSelectedWorkQueueId: (id: number) => void
  setSelectedWorkQueueForAction: (workQueue: CarrierVisitWorkQueueViewModel) => void
  openNameWQDialog: () => void
  onStartWorkQueue: (workQueue: CarrierVisitWorkQueueViewModel) => void
  onStopWorkQueue: (workQueue: CarrierVisitWorkQueueViewModel) => void
}
export const WorkInstructionCranesAccordion = observer(
  ({
    cranes,
    visitStatus,
    selectedCraneId,
    selectedWorkQueueId,
    setSelectedCraneId,
    setSelectedWorkQueueId,
    setSelectedWorkQueueForAction,
    openNameWQDialog,
    onStartWorkQueue,
    onStopWorkQueue,
  }: Props) => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()

    return (
      <>
        {cranes.map(crane => (
          <ControlRoomAccordion
            key={`crane-${crane.id}`}
            isSelected={selectedCraneId === crane.id}
            title={crane.name}
            hasLeftPaddingOnTitle
            hasDetails
            onChange={() => setSelectedCraneId(crane.id)}
            rightSummaryRender={
              <Stack flexDirection='row' alignItems='center' gap={theme.customSpacing.xxs}>
                {crane.status && <WorkQueueStatusChip status={crane.status} />}
                <OptionsButton
                  position='right'
                  options={[
                    {
                      label: t('showAll', 'Show All'),
                      onClick: () => setSelectedCraneId(crane.id),
                    },
                  ]}
                />
              </Stack>
            }
          >
            <Stack divider={<Divider />}>
              {crane.workQueues.map(workQueue => (
                <WorkQueueDetails
                  key={`work-queue-${workQueue.id}`}
                  workQueue={workQueue}
                  isSelected={workQueue.id === selectedWorkQueueId}
                  visitStatus={visitStatus}
                  onClick={() => setSelectedWorkQueueId(workQueue.id)}
                  onStop={() => onStopWorkQueue(workQueue)}
                  onStart={() => onStartWorkQueue(workQueue)}
                  onRename={() => {
                    setSelectedWorkQueueForAction(workQueue)
                    openNameWQDialog()
                  }}
                />
              ))}
            </Stack>

            {crane.workQueues.length === 0 && (
              <Typography
                padding={theme.customSpacing.m}
                paddingX={theme.customSpacing.xl}
                variant='body1'
                color='secondary'
                textAlign='center'
              >
                {t('noWorkQueuesFoundForThisCrane', 'No work queues found for this crane')}
              </Typography>
            )}
          </ControlRoomAccordion>
        ))}

        {cranes.length === 0 && (
          <Typography
            padding={theme.customSpacing.m}
            paddingX={theme.customSpacing.xl}
            variant='body1'
            color='secondary'
            textAlign='center'
          >
            {t('noCranesFoundForThisCarrierVisit', 'No cranes found for this carrier visit')}
          </Typography>
        )}
      </>
    )
  },
)
